import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Input, Tooltip } from 'antd';
import SystemModal from 'components/Shared/Modal';
import Can from 'config/Can';
import useTranslation from 'utils/hooks/useTranslation';

import styles from './style.module.scss';

// The bank reference must be at least this long before we'll let the reviewer
// move to the "Approve and credit" confirmation step.
const MIN_WIRE_TRANSFER_ID_LENGTH = 6;

const FundingRequestReviewModal = ({
  visible,
  onClose,
  fundingRequest,
  onApprove,
  onDecline,
  onDownloadProof,
  isApproving,
  isDeclining,
  isDownloadingProof,
  approveError,
}) => {
  const { translateText } = useTranslation();
  const [confirmed, setConfirmed] = useState(false);
  const [note, setNote] = useState('');
  // Field label is "Wire transfer ID" — kept as depositReference internally to
  // match the approve/reject API's request body field.
  const [depositReference, setDepositReference] = useState('');
  const [idTooShort, setIdTooShort] = useState(false);
  const [approveAsk, setApproveAsk] = useState(false);

  // A failed approve (e.g. a duplicate wire transfer ID) drops the reviewer back
  // to the form, still holding what they typed, so they can fix the id or decline.
  useEffect(() => {
    if (approveError) {
      setApproveAsk(false);
    }
  }, [approveError]);

  const resetAndClose = () => {
    setConfirmed(false);
    setNote('');
    setDepositReference('');
    setIdTooShort(false);
    setApproveAsk(false);
    onClose();
  };

  if (!fundingRequest) {
    return null;
  }

  const { id, account, date, amount, transferReference, proofOfPayment } = fundingRequest;

  const handleDepositReferenceChange = event => {
    setDepositReference(event.target.value);
    setIdTooShort(false);
  };

  const handleApproveClick = () => {
    const trimmedId = depositReference.trim();
    if (!confirmed || !trimmedId) {
      return;
    }
    if (trimmedId.length < MIN_WIRE_TRANSFER_ID_LENGTH) {
      setIdTooShort(true);
      return;
    }
    setApproveAsk(true);
  };

  const handleConfirmApprove = () => {
    onApprove(id, { depositReference: depositReference.trim(), notes: note });
  };

  const handleDecline = () => {
    onDecline(id, { depositReference: depositReference.trim(), notes: note });
  };

  const handleDownloadProof = () => {
    onDownloadProof({ wireTransferId: id, fileName: proofOfPayment });
  };

  if (approveAsk) {
    return (
      <SystemModal
        title={translateText('corporateAccounts.fundingRequests.reviewModal.approveAskTitle', {
          amount,
        })}
        titleLevel={5}
        width={480}
        visible={visible}
        onCancel={resetAndClose}
        footer={null}
        destroyOnClose
        centered
        data-testid="fundingRequestReviewModal"
      >
        <div className={styles.reviewModalAskBody}>
          {translateText('corporateAccounts.fundingRequests.reviewModal.approveAskBody', {
            amount,
            account,
          })}
        </div>
        <div className={styles.reviewModalFooter}>
          <span className={styles.reviewModalButtonWrapper}>
            <Button
              block
              onClick={() => setApproveAsk(false)}
              data-testid="fundingRequestReviewModalApproveAskBack"
            >
              {translateText('corporateAccounts.fundingRequests.reviewModal.goBack')}
            </Button>
          </span>
          <span
            className={`${styles.reviewModalButtonWrapper} ${styles.reviewModalApproveWrapper}`}
          >
            <Button
              type="primary"
              block
              loading={isApproving}
              onClick={handleConfirmApprove}
              className={styles.reviewModalApproveButton}
              data-testid="fundingRequestReviewModalApproveAskConfirm"
            >
              {translateText('corporateAccounts.fundingRequests.reviewModal.confirmApprove')}
            </Button>
          </span>
        </div>
      </SystemModal>
    );
  }

  return (
    <SystemModal
      title={
        <>
          <div className={styles.reviewModalTitle}>
            {translateText('corporateAccounts.fundingRequests.reviewModal.title')}
          </div>
          <div className={styles.reviewModalSubtitle}>
            {translateText('corporateAccounts.fundingRequests.reviewModal.subtitle', {
              id: transferReference,
              date,
            })}
          </div>
        </>
      }
      titleLevel={5}
      width={480}
      visible={visible}
      onCancel={resetAndClose}
      footer={null}
      destroyOnClose
      centered
      data-testid="fundingRequestReviewModal"
    >
      {approveError && (
        <div
          className={styles.reviewModalErrorBanner}
          data-testid="fundingRequestReviewModalApproveError"
        >
          <div className={styles.reviewModalErrorTitle}>
            {translateText('corporateAccounts.fundingRequests.reviewModal.approveError')}
          </div>
          <div>
            {translateText('corporateAccounts.fundingRequests.reviewModal.approveErrorDetail')}
          </div>
        </div>
      )}

      <div className={styles.reviewModalRow}>
        <span className={styles.reviewModalLabel}>
          {translateText('corporateAccounts.fundingRequests.table.account')}
        </span>
        <span className={`${styles.accountName} ${styles.reviewModalValue}`}>{account}</span>
      </div>
      <div className={styles.reviewModalRow}>
        <span className={styles.reviewModalLabel}>
          {translateText('corporateAccounts.fundingRequests.table.amount')}
        </span>
        <span className={styles.reviewModalValue}>{amount}</span>
      </div>
      <div className={styles.reviewModalRow}>
        <span className={styles.reviewModalLabel}>
          {translateText('corporateAccounts.fundingRequests.reviewModal.transferReference')}
        </span>
        <span className={styles.reviewModalValue}>{transferReference}</span>
      </div>

      <div className={styles.reviewModalProof} data-testid="fundingRequestReviewModalProof">
        <div className={styles.reviewModalProofDetails}>
          <div className={styles.reviewModalProofName}>{proofOfPayment}</div>
          <div className={styles.reviewModalProofCaption}>
            {translateText('corporateAccounts.fundingRequests.reviewModal.proofOfPaymentCaption')}
          </div>
        </div>
        <button
          type="button"
          className={styles.proofLink}
          disabled={isDownloadingProof}
          onClick={handleDownloadProof}
          data-testid="fundingRequestReviewModalDownloadProof"
        >
          {translateText('corporateAccounts.fundingRequests.reviewModal.view')}
        </button>
      </div>

      <div className={styles.reviewModalNoteLabel}>
        {translateText('corporateAccounts.fundingRequests.reviewModal.depositReferenceLabel')}
      </div>
      <Input
        value={depositReference}
        onChange={handleDepositReferenceChange}
        placeholder={translateText(
          'corporateAccounts.fundingRequests.reviewModal.depositReferencePlaceholder',
        )}
        className={idTooShort || approveError ? styles.inputError : undefined}
        data-testid="fundingRequestReviewModalDepositReference"
      />
      {idTooShort ? (
        <div
          className={styles.reviewModalFieldError}
          data-testid="fundingRequestReviewModalDepositReferenceError"
        >
          {translateText('corporateAccounts.fundingRequests.reviewModal.depositReferenceTooShort')}
        </div>
      ) : approveError ? (
        <div className={styles.reviewModalFieldError}>
          {translateText('corporateAccounts.fundingRequests.reviewModal.depositReferenceErrorHint')}
        </div>
      ) : (
        <div className={styles.reviewModalNoteCaption}>
          {translateText('corporateAccounts.fundingRequests.reviewModal.depositReferenceCaption')}
        </div>
      )}

      <Checkbox
        checked={confirmed}
        onChange={event => setConfirmed(event.target.checked)}
        className={styles.reviewModalConfirmCheckbox}
        data-testid="fundingRequestReviewModalConfirm"
      >
        {translateText('corporateAccounts.fundingRequests.reviewModal.confirmCheckbox')}
      </Checkbox>

      <div className={styles.reviewModalNoteLabel}>
        {translateText('corporateAccounts.fundingRequests.reviewModal.noteLabel')}
      </div>
      <div className={styles.reviewModalNoteCaption}>
        {translateText('corporateAccounts.fundingRequests.reviewModal.noteCaption')}
      </div>
      <Input.TextArea
        value={note}
        onChange={event => setNote(event.target.value)}
        placeholder={translateText('corporateAccounts.fundingRequests.reviewModal.notePlaceholder')}
        rows={3}
        data-testid="fundingRequestReviewModalNote"
      />

      <div className={styles.reviewModalFooter}>
        <Can I="decline" a="FundingRequest" passThrough>
          {allowed => {
            const isDisabled = !allowed || isDeclining || confirmed || !note.trim();
            const disabledReason = !allowed
              ? translateText(
                  'corporateAccounts.fundingRequests.reviewModal.declineDisabledPermission',
                )
              : confirmed
              ? translateText(
                  'corporateAccounts.fundingRequests.reviewModal.declineDisabledConfirmed',
                )
              : !note.trim()
              ? translateText('corporateAccounts.fundingRequests.reviewModal.declineDisabledNote')
              : null;

            return (
              <Tooltip title={isDisabled ? disabledReason : null}>
                <span className={styles.reviewModalButtonWrapper}>
                  <Button
                    danger
                    ghost
                    block
                    disabled={isDisabled}
                    loading={isDeclining}
                    onClick={handleDecline}
                    className={styles.reviewModalDeclineButton}
                    data-testid="fundingRequestReviewModalDecline"
                  >
                    {translateText('corporateAccounts.fundingRequests.reviewModal.decline')}
                  </Button>
                </span>
              </Tooltip>
            );
          }}
        </Can>
        <Can I="approve" a="FundingRequest" passThrough>
          {allowed => {
            const isDisabled = !allowed || !confirmed || isApproving || !depositReference.trim();
            const disabledReason = !allowed
              ? translateText(
                  'corporateAccounts.fundingRequests.reviewModal.approveDisabledPermission',
                )
              : !confirmed
              ? translateText(
                  'corporateAccounts.fundingRequests.reviewModal.approveDisabledConfirm',
                )
              : !depositReference.trim()
              ? translateText(
                  'corporateAccounts.fundingRequests.reviewModal.depositReferenceRequired',
                )
              : null;

            return (
              <Tooltip title={isDisabled ? disabledReason : null}>
                <span
                  className={`${styles.reviewModalButtonWrapper} ${styles.reviewModalApproveWrapper}`}
                >
                  <Button
                    type="primary"
                    block
                    disabled={isDisabled}
                    onClick={handleApproveClick}
                    className={styles.reviewModalApproveButton}
                    data-testid="fundingRequestReviewModalApprove"
                  >
                    {translateText('corporateAccounts.fundingRequests.reviewModal.approve')}
                  </Button>
                </span>
              </Tooltip>
            );
          }}
        </Can>
      </div>
    </SystemModal>
  );
};

export default FundingRequestReviewModal;
